import { Modal, Button, message } from "antd";
import { forwardRef, useImperativeHandle, useRef, useState, useCallback } from "react";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import "./index.scss";

// 支持透明背景色的图片格式（保持原格式输出，背景透明）
const TRANSPARENT_SUPPORTED_EXTS = ["png", "webp", "gif"];
// 不支持透明背景色的图片格式（统一转为 jpg，填充白色背景）
const TRANSPARENT_UNSUPPORTED_EXTS = ["jpg", "jpeg", "bmp"];

const WdImageUploadInput = forwardRef(
  (
    {
      onSuccess,
      modalWidth = 350,
      cropBoxWidth = 200,
      cropBoxHeight = 200,
      viewWidth = 300,
      viewHeight = 300,
      fileType = ["jpg", "jpeg", "png", "bmp"],
      maxFileSize = 1 * 1024 * 1024,
      outputQuality = 0.9,
      disabled = false,
      overlayColor = "rgba(240, 242, 245, 0.85)",
      backgroundColor = "#ffffff",
    },
    ref,
  ) => {
    const fileInputRef = useRef(null);
    const cropperRef = useRef(null);
    const [imageUrl, setImageUrl] = useState("");
    const [visible, setVisible] = useState(false);
    // 记录当前正在裁剪的源文件扩展名（小写，如 png/jpg/bmp...）
    const [sourceExt, setSourceExt] = useState("");

    // 暴露 upload 方法给外部调用
    useImperativeHandle(ref, () => ({
      upload: () => {
        if (!disabled) {
          fileInputRef.current?.click();
        }
      },
    }));

    // 验证文件类型
    const validateFileType = useCallback(
      (file) => {
        const ext = file.name.split(".").pop()?.toLowerCase() || "";
        return fileType.map((t) => t.toLowerCase()).includes(ext);
      },
      [fileType],
    );

    // 验证文件大小
    const validateFileSize = useCallback(
      (file) => {
        return file.size <= maxFileSize;
      },
      [maxFileSize],
    );

    // 文件选择处理
    const handleFileChange = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // 验证是否为图片
      if (!file.type.startsWith("image/")) {
        message.error("请上传图片文件");
        return;
      }

      // 验证文件类型
      if (!validateFileType(file)) {
        message.error(`仅支持 ${fileType.join("、")} 格式的图片`);
        return;
      }

      // 验证文件大小
      if (!validateFileSize(file)) {
        const maxMB = (maxFileSize / (1024 * 1024)).toFixed(1);
        message.error(`图片大小不能超过 ${maxMB}MB`);
        return;
      }

      const ext = file.name.split(".").pop()?.toLowerCase() || "";
      const reader = new FileReader();
      reader.onload = (event) => {
        setImageUrl(event.target?.result);
        setSourceExt(ext);
        setVisible(true);
      };
      reader.readAsDataURL(file);

      // 重置 input 值，允许重复选择同一文件
      e.target.value = "";
    };

    // 裁剪框准备就绪时的处理
    const handleCropperReady = () => {
      const cropper = cropperRef.current?.cropper;
      if (!cropper) return;

      const containerData = cropper.getContainerData();
      const containerWidth = containerData.width;
      const containerHeight = containerData.height;

      const imageData = cropper.getImageData();
      const naturalWidth = imageData.naturalWidth;
      const naturalHeight = imageData.naturalHeight;

      const { width: cropSizeWidth, height: cropSizeHeight } = {
        width: cropBoxWidth,
        height: cropBoxHeight,
      };

      // 裁剪框居中
      const cropBoxLeft = (containerWidth - cropSizeWidth) / 2;
      const cropBoxTop = (containerHeight - cropSizeHeight) / 2;

      if (naturalWidth > naturalHeight) {
        const scale = cropSizeHeight / naturalHeight;
        const canvasWidth = naturalWidth * scale;
        const canvasLeft = cropBoxLeft + (cropSizeWidth - canvasWidth) / 2;

        cropper.setCanvasData({
          left: canvasLeft,
          top: cropBoxTop,
          width: canvasWidth,
          height: cropSizeHeight,
        });
      } else if (naturalWidth < naturalHeight) {
        const scale = cropSizeWidth / naturalWidth;
        const canvasHeight = naturalHeight * scale;
        const canvasTop = cropBoxTop + (cropSizeHeight - canvasHeight) / 2;

        cropper.setCanvasData({
          left: cropBoxLeft,
          top: canvasTop,
          width: cropSizeWidth,
          height: canvasHeight,
        });
      }

      // 裁剪框居中设置
      cropper.setCropBoxData({
        left: cropBoxLeft,
        top: cropBoxTop,
        width: cropSizeWidth,
        height: cropSizeHeight,
      });
    };

    // 取消处理
    const handleCancel = () => {
      setVisible(false);
      setImageUrl("");
      setSourceExt("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };

    // 确认裁剪
    const handleConfirm = () => {
      const cropper = cropperRef.current?.cropper;
      if (!cropper) return;

      const croppedCanvas = cropper.getCroppedCanvas({
        width: cropBoxWidth,
        height: cropBoxHeight,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: "high",
        fillColor: "transparent",
      });

      if (!croppedCanvas) {
        message.error("裁剪失败，请重试");
        return;
      }

      // 根据源文件格式决定输出策略：
// - 支持透明背景色的格式（png/webp/gif）：保持原格式输出，背景透明
// - 不支持透明背景色的格式（jpg/jpeg/bmp 等）：统一转为 jpg，并填充指定背景色（默认白色）
      const isTransparentSupported = TRANSPARENT_SUPPORTED_EXTS.includes(sourceExt);
      let outputCanvas = croppedCanvas;
      let outputType;
      let outputFileExt;

      if (isTransparentSupported) {
        outputType = "image/png";
        outputFileExt = sourceExt || "png";
      } else {
        const ctx = croppedCanvas.getContext("2d");
        if (ctx) {
          ctx.globalCompositeOperation = "destination-over";
          ctx.fillStyle = backgroundColor || "#ffffff";
          ctx.fillRect(0, 0, croppedCanvas.width, croppedCanvas.height);
        }
        outputType = "image/jpeg";
        outputFileExt = "jpg";
      }

      outputCanvas.toBlob(
        (blob) => {
          if (blob) {
            const fileName = `cropped_${Date.now()}.${outputFileExt}`;
            const file = new File([blob], fileName, { type: outputType });
            handleCancel();
            onSuccess?.(file);
          }
        },
        outputType,
        outputQuality,
      );
    };

    return (
      <>
        <input
          style={{ display: "none" }}
          type="file"
          ref={fileInputRef}
          accept="image/*"
          onChange={handleFileChange}
          disabled={disabled}
        />

        <Modal
          title={
            <div style={{ textAlign: "center" }}>
              <span>编辑图片</span>
              <div style={{ fontSize: 12, color: "#999", fontWeight: 500, margin: '10px 0 20px 0' }}>
                调整图片尺寸和位置
              </div>
            </div>
          }
          open={visible}
          visible={visible}
          centered
          onCancel={handleCancel}
          width={modalWidth}
          footer={<div style={{ display: "flex", marginTop: '20px', justifyContent: "center", gap: 10 }}>
            <Button key="cancel" onClick={handleCancel}>
              取消
            </Button>
            <Button key="crop" type="primary" onClick={handleConfirm}>
              确认
            </Button>
          </div>}
          destroyOnClose
          maskClosable={false}
          className="wd-image-upload-input-modal"
        >
          <div className="cropper-container" style={{ "--cropper-overlay-color": overlayColor }}>
            <Cropper
              ref={cropperRef}
              src={imageUrl}
              style={{ width: viewWidth, height: viewHeight }}
              aspectRatio={cropBoxWidth / cropBoxHeight}
              viewMode={1}
              dragMode="move"
              guides={false}
              center={false}
              highlight={false}
              background={false}
              responsive={false}
              autoCropArea={1}
              minCropBoxWidth={cropBoxWidth}
              minCropBoxHeight={cropBoxHeight}
              cropBoxResizable={false}
              cropBoxMovable={false}
              ready={handleCropperReady}
            />
          </div>
        </Modal>
      </>
    );
  },
);

WdImageUploadInput.displayName = "ImgUploadInput";

export default WdImageUploadInput;
